import { useState } from 'react';
import {
  Container,
  Group,
  Title,
  Text,
  Table,
  Badge,
  Button,
  SimpleGrid,
  Card,
  Stack,
  Modal,
  Skeleton,
  ScrollArea,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconEye } from '@tabler/icons-react';
import {
  useVoiceCalls,
  useVoiceCallDetail,
  useVoiceMetrics,
} from '@/hooks/usePortalApi';

const DIRECTION_COLORS: Record<string, string> = {
  inbound: 'blue',
  outbound: 'green',
};

const OUTCOME_COLORS: Record<string, string> = {
  completed: 'green',
  no_answer: 'yellow',
  busy: 'orange',
  failed: 'red',
  voicemail: 'violet',
};

const SENTIMENT_COLORS: Record<string, string> = {
  positive: 'green',
  neutral: 'gray',
  negative: 'red',
};

export default function VoiceCallHistory() {
  const [opened, { open, close }] = useDisclosure(false);
  const [selectedCallSid, setSelectedCallSid] = useState<string | null>(null);

  const { data: calls, isLoading: callsLoading } = useVoiceCalls();
  const { data: detail } = useVoiceCallDetail(selectedCallSid);
  const { data: metrics, isLoading: metricsLoading } = useVoiceMetrics();

  const handleView = (callSid: string) => {
    setSelectedCallSid(callSid);
    open();
  };

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const truncate = (text: string, maxLength: number) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  return (
    <Container size="lg" py="md">
      <Group justify="space-between" mb="md">
        <Stack gap="xs">
          <Title order={2}>Voice Calls</Title>
          <Text size="sm" c="dimmed">
            AI voice agent interaction history
          </Text>
        </Stack>
      </Group>

      <SimpleGrid cols={4} mb="xl">
        {metricsLoading ? (
          <>
            <Skeleton height={100} />
            <Skeleton height={100} />
            <Skeleton height={100} />
            <Skeleton height={100} />
          </>
        ) : (
          <>
            <Card withBorder>
              <Stack gap="xs">
                <Text size="sm" c="dimmed">
                  Calls Today
                </Text>
                <Text size="xl" fw={700}>
                  {metrics?.calls_today || 0}
                </Text>
              </Stack>
            </Card>
            <Card withBorder>
              <Stack gap="xs">
                <Text size="sm" c="dimmed">
                  Avg Duration
                </Text>
                <Text size="xl" fw={700}>
                  {formatDuration(metrics?.avg_duration || 0)}
                </Text>
              </Stack>
            </Card>
            <Card withBorder>
              <Stack gap="xs">
                <Text size="sm" c="dimmed">
                  Positive Sentiment %
                </Text>
                <Text size="xl" fw={700}>
                  {metrics?.positive_sentiment_pct || 0}%
                </Text>
              </Stack>
            </Card>
            <Card withBorder>
              <Stack gap="xs">
                <Text size="sm" c="dimmed">
                  Active Calls
                </Text>
                <Text size="xl" fw={700}>
                  {metrics?.active_calls || 0}
                </Text>
              </Stack>
            </Card>
          </>
        )}
      </SimpleGrid>

      {callsLoading ? (
        <Stack gap="xs">
          <Skeleton height={50} />
          <Skeleton height={50} />
          <Skeleton height={50} />
        </Stack>
      ) : !calls || calls.length === 0 ? (
        <Text ta="center" c="dimmed" py="xl">
          No voice calls found
        </Text>
      ) : (
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Date/Time</Table.Th>
              <Table.Th>Direction</Table.Th>
              <Table.Th>Duration</Table.Th>
              <Table.Th>Outcome</Table.Th>
              <Table.Th>Sentiment</Table.Th>
              <Table.Th>Summary Preview</Table.Th>
              <Table.Th>Actions</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {calls.map((call: any) => (
              <Table.Tr key={call.call_sid}>
                <Table.Td>
                  {new Date(call.timestamp).toLocaleString()}
                </Table.Td>
                <Table.Td>
                  <Badge color={DIRECTION_COLORS[call.direction]} variant="light">
                    {call.direction}
                  </Badge>
                </Table.Td>
                <Table.Td>{formatDuration(call.duration)}</Table.Td>
                <Table.Td>
                  <Badge color={OUTCOME_COLORS[call.outcome]} variant="light">
                    {call.outcome}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  <Badge
                    color={SENTIMENT_COLORS[call.sentiment]}
                    variant="light"
                  >
                    {call.sentiment}
                  </Badge>
                </Table.Td>
                <Table.Td>{truncate(call.summary || '', 60)}</Table.Td>
                <Table.Td>
                  <Button
                    size="xs"
                    variant="subtle"
                    leftSection={<IconEye size={14} />}
                    onClick={() => handleView(call.call_sid)}
                  >
                    View
                  </Button>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}

      <Modal opened={opened} onClose={close} title="Call Details" size="lg">
        {detail && (
          <Stack gap="md">
            <Group justify="space-between">
              <Text size="sm" c="dimmed">
                Direction
              </Text>
              <Badge color={DIRECTION_COLORS[detail.direction]} variant="light">
                {detail.direction}
              </Badge>
            </Group>

            <Group justify="space-between">
              <Text size="sm" c="dimmed">
                Duration
              </Text>
              <Text fw={600}>{formatDuration(detail.duration)}</Text>
            </Group>

            <Group justify="space-between">
              <Text size="sm" c="dimmed">
                From
              </Text>
              <Text fw={600}>{detail.from_number}</Text>
            </Group>

            <Group justify="space-between">
              <Text size="sm" c="dimmed">
                To
              </Text>
              <Text fw={600}>{detail.to_number}</Text>
            </Group>

            <Group justify="space-between">
              <Text size="sm" c="dimmed">
                Outcome
              </Text>
              <Badge color={OUTCOME_COLORS[detail.outcome]} variant="light">
                {detail.outcome}
              </Badge>
            </Group>

            <Group justify="space-between">
              <Text size="sm" c="dimmed">
                Sentiment Score
              </Text>
              <Text fw={600}>{detail.sentiment_score}%</Text>
            </Group>

            {detail.tags && detail.tags.length > 0 && (
              <Group>
                <Text size="sm" c="dimmed">
                  Tags
                </Text>
                <Group gap="xs">
                  {detail.tags.map((tag: string, idx: number) => (
                    <Badge key={idx} size="sm" variant="outline">
                      {tag}
                    </Badge>
                  ))}
                </Group>
              </Group>
            )}

            <Stack gap="xs">
              <Text size="sm" fw={600}>
                AI Summary
              </Text>
              <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
                {detail.summary}
              </Text>
            </Stack>

            <Stack gap="xs">
              <Text size="sm" fw={600}>
                Full Transcript
              </Text>
              <ScrollArea h={200}>
                <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
                  {detail.transcript}
                </Text>
              </ScrollArea>
            </Stack>
          </Stack>
        )}
      </Modal>
    </Container>
  );
}
